import { useEffect, useState, useRef, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import type { ReplayData } from "../../shared/types";
import { api } from "../lib/network";
import { getBoardAtTime, hasSubmitted, formatDuration, PLAYBACK_DURATION_MS } from "../lib/replay";
import ReplayBoard from "../components/game/ReplayBoard";
import { GridCluePanel, ArcadeWordPanel } from "../components/game/CluePanels";

export default function Replay() {
  const { roomId } = useParams<{ roomId: string }>();
  const navigate = useNavigate();
  const [replay, setReplay] = useState<ReplayData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const [isPlaying, setIsPlaying] = useState(false);
  const [progress, setProgress] = useState(0); // 0-1
  const [speed, setSpeed] = useState(1);
  const animationRef = useRef<number>(0);
  const startTimeRef = useRef<number>(0);
  const pausedProgressRef = useRef<number>(0);

  useEffect(() => {
    let cancelled = false;
    async function load(attempt: number) {
      if (!roomId) return;
      try {
        const data = await api.getReplay(roomId);
        if (cancelled) return;
        setReplay(data);
        setLoading(false);
      } catch (e) {
        if (cancelled) return;
        // The replay is written by the worker after game over; brief retries
        // absorb the window where it isn't in KV yet.
        if (attempt < 10) {
          setTimeout(() => load(attempt + 1), 700);
          return;
        }
        setError(e instanceof Error ? e.message : "Replay not found");
        setLoading(false);
      }
    }
    load(0);
    return () => {
      cancelled = true;
    };
  }, [roomId]);

  const animate = useCallback(() => {
    const elapsed = (Date.now() - startTimeRef.current) * speed;
    const totalDuration = PLAYBACK_DURATION_MS;
    const newProgress = Math.min(1, pausedProgressRef.current + elapsed / totalDuration);
    setProgress(newProgress);

    if (newProgress < 1) {
      animationRef.current = requestAnimationFrame(animate);
    } else {
      setIsPlaying(false);
    }
  }, [speed]);

  const togglePlay = useCallback(() => {
    if (isPlaying) {
      cancelAnimationFrame(animationRef.current);
      pausedProgressRef.current = progress;
      setIsPlaying(false);
    } else {
      if (progress >= 1) {
        pausedProgressRef.current = 0;
        setProgress(0);
      }
      startTimeRef.current = Date.now();
      setIsPlaying(true);
      animationRef.current = requestAnimationFrame(animate);
    }
  }, [isPlaying, progress, animate]);

  const handleScrub = useCallback(
    (value: number) => {
      const newProgress = Math.min(1, Math.max(0, value));
      if (isPlaying) {
        cancelAnimationFrame(animationRef.current);
        setIsPlaying(false);
      }
      pausedProgressRef.current = newProgress;
      setProgress(newProgress);
    },
    [isPlaying]
  );

  useEffect(() => {
    return () => {
      cancelAnimationFrame(animationRef.current);
    };
  }, []);

  if (loading) {
    return (
      <div className="w-full h-full flex items-center justify-center">
        <div className="text-vsw-300">Loading replay...</div>
      </div>
    );
  }

  if (error || !replay) {
    return (
      <div className="w-full h-full flex flex-col items-center justify-center gap-4">
        <div className="text-red-400">{error || "Replay not available"}</div>
        <button
          onClick={() => navigate("/")}
          className="bg-vsw-500 text-white px-4 py-2 rounded"
        >
          Back to Home
        </button>
      </div>
    );
  }

  const gameDuration = replay.endedAt - replay.startedAt;
  const currentTimeMs = progress * gameDuration;

  const p1Board = getBoardAtTime(replay.players.p1.moves, currentTimeMs);
  const p2Board = getBoardAtTime(replay.players.p2.moves, currentTimeMs);

  const p1Submitted = hasSubmitted(replay.players.p1.moves, currentTimeMs);
  const p2Submitted = hasSubmitted(replay.players.p2.moves, currentTimeMs);

  const boardWidth = replay.puzzle?.width || 15;
  const boardHeight = replay.puzzle?.height || 15;

  return (
    <div className="w-full h-full flex flex-col bg-gray-950 relative overflow-hidden">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-2 bg-board-cell border-b border-board-border">
        <div className="flex items-center gap-3">
          <h1 className="text-lg font-bold text-vsw-400">Replay</h1>
          <span className="text-vsw-300 text-sm">
            {replay.mode} · {replay.complexity} · {formatDuration(replay.timeLimitMs)}
          </span>
        </div>
        <button
          onClick={() => navigate("/")}
          className="text-sm text-vsw-300 hover:text-vsw-200"
        >
          Close
        </button>
      </div>

      {/* Boards */}
      <div className="flex-1 flex items-center justify-center gap-8 p-4 overflow-auto">
        {/* P1 */}
        <div className="flex flex-col items-center gap-2">
          <div className="flex items-center gap-2">
            <span className="text-vsw-200 font-medium">{replay.players.p1.username}</span>
            <span className="text-xs text-vsw-300">
              {p1Submitted ? "Submitted" : "Playing"}
            </span>
          </div>
          <ReplayBoard
            width={boardWidth}
            height={boardHeight}
            tiles={p1Board}
            premiumSquares={replay.premiumSquares}
            puzzle={replay.puzzle}
          />
          <div className="text-vsw-400 font-bold">
            Score: {replay.players.p1.finalScore}
          </div>
        </div>

        {/* VS */}
        <div className="text-vsw-300 font-bold text-xl">VS</div>

        {/* P2 */}
        <div className="flex flex-col items-center gap-2">
          <div className="flex items-center gap-2">
            <span className="text-vsw-200 font-medium">{replay.players.p2.username}</span>
            <span className="text-xs text-vsw-300">
              {p2Submitted ? "Submitted" : "Playing"}
            </span>
          </div>
          <ReplayBoard
            width={boardWidth}
            height={boardHeight}
            tiles={p2Board}
            premiumSquares={replay.premiumSquares}
            puzzle={replay.puzzle}
          />
          <div className="text-vsw-400 font-bold">
            Score: {replay.players.p2.finalScore}
          </div>
        </div>

        {/* Hints beside the boards on narrower desktops (upper-right panel takes over at ≥1400px) */}
        <div className="min-[1400px]:hidden w-64 max-w-[40vw] shrink-0">
          {replay.puzzle && (
            <div className="max-h-full overflow-y-auto">
              {replay.mode === "grid" && (
                <GridCluePanel clues={replay.puzzle.clues} solvedWords={{}} />
              )}
              {replay.mode === "arcade" && (
                <ArcadeWordPanel puzzle={replay.puzzle} solvedWords={{}} />
              )}
            </div>
          )}
        </div>
      </div>

      {/* Controls */}
      <div className="bg-board-cell border-t border-board-border px-4 py-3 space-y-2">
        {/* Scrub bar */}
        <input
          type="range"
          min={0}
          max={1000}
          step={1}
          value={Math.round(progress * 1000)}
          onChange={(e) => handleScrub(Number(e.target.value) / 1000)}
          aria-label="Replay position"
          className="w-full h-2 accent-vsw-500 cursor-pointer"
        />

        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button
              onClick={togglePlay}
              className="bg-vsw-500 text-white px-4 py-1.5 rounded font-medium hover:bg-vsw-400 text-sm"
            >
              {isPlaying ? "Pause" : progress >= 1 ? "Replay" : "Play"}
            </button>

            <div className="flex gap-1">
              {[1, 2, 4].map((s) => (
                <button
                  key={s}
                  onClick={() => {
                    setSpeed(s);
                    if (isPlaying) {
                      pausedProgressRef.current = progress;
                      startTimeRef.current = Date.now();
                    }
                  }}
                  className={`px-2 py-1 rounded text-xs font-medium ${
                    speed === s ? "bg-vsw-500 text-white" : "bg-board-bg text-vsw-300"
                  }`}
                >
                  {s}x
                </button>
              ))}
            </div>
          </div>

          <div className="text-vsw-300 text-sm">
            {Math.floor(progress * (gameDuration / 1000))}s / {Math.floor(gameDuration / 1000)}s
          </div>
        </div>
      </div>

      {/* Hints in the upper-right corner on wide screens (kept out of flow so the boards stay centered) */}
      {replay.puzzle && (
        <div className="hidden min-[1400px]:block absolute top-2 right-2 z-10 w-56 max-h-[calc(100%-5rem)] overflow-y-auto">
          {replay.mode === "grid" && (
            <GridCluePanel clues={replay.puzzle.clues} solvedWords={{}} />
          )}
          {replay.mode === "arcade" && (
            <ArcadeWordPanel puzzle={replay.puzzle} solvedWords={{}} />
          )}
        </div>
      )}
    </div>
  );
}
